import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { getToken, setToken, clearToken, saveRememberedEmail } from "@/lib/authStorage";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasActiveLicense, setHasActiveLicense] = useState(false);
  const [activeLicense, setActiveLicense] = useState(null);

  const refreshLicense = useCallback(async () => {
    try {
      const { data } = await api.get("/licenses/me/active");
      setHasActiveLicense(!!data.active);
      setActiveLicense(data.license);
    } catch {
      setHasActiveLicense(false);
      setActiveLicense(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshLicense();
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshLicense]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password, rememberDevice = false) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token, rememberDevice);
    saveRememberedEmail(email, rememberDevice);
    setUser(data.user);
    await refreshLicense();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.token, true);
    setUser(data.user);
    await refreshLicense();
    return data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setHasActiveLicense(false);
    setActiveLicense(null);
  };

  return (
    <AuthCtx.Provider
      value={{ user, loading, login, register, logout, reload: loadMe, hasActiveLicense, activeLicense, refreshLicense }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
